import type { PipelineBoard, PipelineCard } from '@/pages/pipelines/common/types';

import type { InvestmentPipelineBoardFilters } from '../types';
import { getInvestmentIntroductionTypeMatches } from './normalize-card';
import { normalizeInvestmentTimerFilterValue } from './timers';

export const filterInvestmentBoardCards = (
  board: PipelineBoard,
  filters: Pick<InvestmentPipelineBoardFilters, 'introductionTypes' | 'timerStatuses'>,
): PipelineBoard => {
  const introductionTypes = new Set(filters.introductionTypes ?? []);
  const timerStatuses = new Set(
    (filters.timerStatuses ?? []).map(normalizeInvestmentTimerFilterValue),
  );

  if (introductionTypes.size === 0 && timerStatuses.size === 0) return board;

  const columns = board.columns.map((column) => {
    const cards = column.cards.filter(
      (card) =>
        matchesIntroductionTypes(card, introductionTypes) &&
        matchesTimerStatuses(card, timerStatuses),
    );

    return cards.length === column.cards.length
      ? column
      : { ...column, cards, count: cards.length };
  });
  const cards = columns.flatMap((column) => column.cards);

  return {
    ...board,
    columns,
    totals: {
      cards: cards.length,
      activeCards: cards.filter((card) => card.active).length,
      nonActiveCards: cards.filter((card) => !card.active).length,
    },
  };
};

const matchesIntroductionTypes = (card: PipelineCard, filters: Set<string>) => {
  if (filters.size === 0) return true;

  const cardTypes = getInvestmentIntroductionTypeMatches(card);

  return [...filters].some((filter) => cardTypes.has(filter));
};

const matchesTimerStatuses = (card: PipelineCard, filters: Set<string>) => {
  if (filters.size === 0) return true;
  if (!card.timer) return filters.has('none');
  if (filters.has('active') && card.active) return true;

  return filters.has(normalizeInvestmentTimerFilterValue(card.timer.tone));
};
